/**
 * LA RELECTURE D'UN TRACÉ ATTENDU — chantier « Élève », lot 8, palier L0
 * (« voir avant de produire », AB#943 ; `docs/pilotage/atelier-figures/
 * CONCEPTION.md`, § 5.1).
 *
 * En relecture, un exercice à tracer se lisait « Un tracé : 3 objets attendus,
 * 2 contraintes vérifiées ». Le relecteur ne voyait ni la solution, ni ce qui
 * est vérifié, ni les tracés fautifs prévus. Tout ce que l'écran en DÉCIDE vit
 * ici, pour être éprouvé sans navigateur :
 *
 *  - l'exercice de tracé, relu depuis la réponse de relecture (`GET
 *    /v1/tuteur/manuel/exercices/:code`) — elle porte déjà le corrigé ENTIER
 *    (solution, contraintes, tracés fautifs) : aucun changement de serveur ;
 *  - la solution du prof, étape par étape, sur la figure de l'énoncé ;
 *  - chaque tracé fautif, construit et rejoué (`eprouverTracesFautifs`) ;
 *  - le BAC À SABLE : les gestes d'un élève (toucher, annuler, effacer) en
 *    fonctions pures, et le jugement (`jugerTrace`), le MÊME que le serveur.
 *
 * ─── Aucun modèle (invariant I-11) ──────────────────────────────────────────
 *
 * Rien ici n'écrit ni n'appelle : le vérificateur et le jugement sont des
 * fonctions pures du paquet partagé. Le bac à sable ne garde rien.
 *
 * Les dessins sont ceux de `figures-v2-web.ts` (`superposerLesTraces`,
 * `listeVersSvg`, la palette de la console) : le moteur du produit.
 */
import {
  aimanter,
  decrireConstruction,
  dessinerPlanAvecEtiquettes,
  documentDeTrace,
  enonceDeTrace,
  eprouverTracesFautifs,
  estIdEnonce,
  jugerTrace,
  listeVersSvg,
  repereDeTrace,
  resoudreConstruction,
  superposerLesTraces,
  versFigureV2,
  LIBELLE_OUTIL,
  type CorrigeTrace,
  type DocumentConstructionDto,
  type EpreuveDeTraceFautif,
  type ExerciceDeTrace,
  type ExerciceRelectureResponse,
  type FigureV2Plan,
  type JugementDeTrace,
  type ObjetConstructionDto,
  type OutilTrace,
  type PointV2,
  type RepereV2Dto,
} from '@silex/shared';

import { PALETTE_CONSOLE } from './figures-v2-web';

/* ─────────────────────────── l'exercice ─────────────────────────── */

/** Un exercice de tracé, tel que la relecture le lit — et ce qu'il faut pour le dessiner. */
export interface TraceRelu {
  readonly exercice: ExerciceDeTrace;
  readonly corrige: CorrigeTrace;
  readonly enonce: FigureV2Plan | null;
  readonly repere: RepereV2Dto;
  /** Les outils que l'atelier propose à l'élève pour cet exercice. */
  readonly outils: readonly OutilTrace[];
  /** Le titre d'une erreur, par son code ; le code lui-même à défaut. */
  readonly titreErreur: (code: string) => string;
}

/**
 * L'exercice de tracé d'une réponse de relecture ; `null` : ce n'est pas un
 * tracé, ou il n'a ni figure d'énoncé ni repère (il ne se corrige pas).
 */
export function traceRelu(d: ExerciceRelectureResponse): TraceRelu | null {
  if (d.corrige.nature !== 'trace') return null;
  const corrige = d.corrige;
  const enonce = enonceDeTrace(d.exercice.figure);
  const repere = repereDeTrace(corrige, enonce);
  if (repere === null) return null;
  const titres = new Map<string, string>();
  for (const f of d.reponsesFautives) if (f.erreur) titres.set(f.erreur.code, f.erreur.titre);
  return {
    exercice: {
      corrige,
      figure: d.exercice.figure ?? null,
      /* Les réponses fautives qui nomment une erreur : la clé d'un tracé fautif est leur libellé. */
      fautives: d.reponsesFautives.flatMap((f) =>
        f.erreur === null
          ? []
          : [{ reponse: f.reponse, erreurCode: f.erreur.code, cheminement: f.cheminement }],
      ),
    },
    corrige,
    enonce,
    repere,
    outils: d.exercice.saisie.nature === 'trace' ? d.exercice.saisie.outils : corrige.outils,
    titreErreur: (code) => titres.get(code) ?? code,
  };
}

/** Un document de l'élève (ou de la solution), sur le repère de l'exercice. */
export function documentDuTrace(
  t: Pick<TraceRelu, 'repere' | 'enonce'>,
  objets: readonly ObjetConstructionDto[],
): DocumentConstructionDto {
  return documentDeTrace(t.repere, { objets, calque: t.enonce !== null });
}

const solutionDe = (t: TraceRelu): readonly ObjetConstructionDto[] =>
  t.corrige.solution as unknown as readonly ObjetConstructionDto[];

/**
 * Deux couches sur la figure de l'énoncé : `plein` au ton de l'élève, et
 * `pointille` (la solution, sous un tracé fautif ou après un essai) au ton
 * attendu. La règle de superposition est celle de l'application.
 */
export function svgDuTrace(
  t: Pick<TraceRelu, 'repere' | 'enonce'>,
  plein: readonly ObjetConstructionDto[],
  pointille: readonly ObjetConstructionDto[] | null,
  titre: string,
): string {
  const { liste } = superposerLesTraces(
    documentDuTrace(t, plein),
    pointille === null ? null : documentDuTrace(t, pointille),
    t.enonce,
  );
  return listeVersSvg(liste, { couleurs: PALETTE_CONSOLE, classe: 'figure-v2__svg', titre });
}

/* ─────────────────────────── la solution, pas à pas ─────────────────────────── */

export interface EtapeDeSolution {
  /** Les objets posés jusqu'à cette étape comprise. */
  readonly objets: readonly ObjetConstructionDto[];
  /** Ce que l'étape pose, en une phrase (`decrireConstruction`). */
  readonly phrase: string;
}

/** La solution du prof, une étape par objet, dans l'ordre où elle est écrite. */
export function etapesDeLaSolution(t: TraceRelu): readonly EtapeDeSolution[] {
  const solution = solutionDe(t);
  const doc = documentDuTrace(t, solution);
  const { phrases } = decrireConstruction(doc, resoudreConstruction(doc, t.enonce));
  return solution.map((o, i) => ({
    objets: solution.slice(0, i + 1),
    phrase: phrases.find((p) => p.id === o.id)?.texte ?? `Objet ${o.id}.`,
  }));
}

/** Le jugement de la solution du prof elle-même : elle doit être jugée juste. */
export function jugerLaSolution(t: TraceRelu): JugementDeTrace {
  return jugerTrace(t.exercice, solutionDe(t));
}

/* ─────────────────────────── les tracés fautifs ─────────────────────────── */

/** Chaque tracé fautif prévu, construit et rejoué par le jugement du serveur. */
export function tracesFautifsRejoues(t: TraceRelu): readonly EpreuveDeTraceFautif[] {
  return eprouverTracesFautifs(t.exercice);
}

/* ─────────────────────────── le bac à sable ─────────────────────────── */

/** Les outils que le bac à sable sait servir au clic. `fonction` et `demi_plan` passent par un formulaire. */
export const OUTILS_AU_CLIC = [
  'point',
  'segment',
  'droite',
  'vecteur',
  'cercle',
  'polygone',
  'angle',
] as const;
export type OutilAuClic = (typeof OUTILS_AU_CLIC)[number];

export const NOM_OUTIL: Readonly<Record<OutilTrace, string>> = {
  point: 'Point',
  droite: 'Droite',
  segment: 'Segment',
  cercle: 'Cercle',
  polygone: 'Polygone',
  fonction: 'Fonction',
  demi_plan: 'Demi-plan',
  vecteur: 'Vecteur',
  angle: 'Angle',
  /* Atelier de figures, L3 : les outils de construction, nommés comme la barre
     de l'élève (`LIBELLE_OUTIL`). Le bac à sable ne les sert pas encore au clic. */
  compas: LIBELLE_OUTIL.compas,
  milieu: LIBELLE_OUTIL.milieu,
  perpendiculaire: LIBELLE_OUTIL.perpendiculaire,
  parallele: LIBELLE_OUTIL.parallele,
  mediatrice: LIBELLE_OUTIL.mediatrice,
  bissectrice: LIBELLE_OUTIL.bissectrice,
  intersection: LIBELLE_OUTIL.intersection,
  translation: LIBELLE_OUTIL.translation,
  rotation: LIBELLE_OUTIL.rotation,
  reflexion: LIBELLE_OUTIL.reflexion,
  homothetie: LIBELLE_OUTIL.homothetie,
  /* Atelier de figures, L6 (F8) : l'esquisse. Le bac à sable ne la sert pas au clic. */
  esquisse: LIBELLE_OUTIL.esquisse,
};

/** Combien de points un outil attend avant de poser son objet (le polygone : jusqu'à ce qu'on le ferme). */
const POINTS_PAR_OUTIL: Readonly<Record<OutilAuClic, number>> = {
  point: 1,
  segment: 2,
  droite: 2,
  vecteur: 2,
  cercle: 2,
  angle: 3,
  polygone: Number.POSITIVE_INFINITY,
};

export interface BacASable {
  readonly objets: readonly ObjetConstructionDto[];
  /** Les points déjà touchés par l'outil en cours (identifiants dans `objets`). */
  readonly enCours: readonly string[];
  /** Les états précédents de `objets`, pour Annuler. */
  readonly passe: readonly (readonly ObjetConstructionDto[])[];
}

export const BAC_VIDE: BacASable = { objets: [], enCours: [], passe: [] };

const LETTRES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Le premier nom de point libre, comme sur une feuille : A…Z, puis A1… (ceux de l'énoncé exclus). */
export function nomLibre(pris: ReadonlySet<string>): string {
  for (let tour = 0; ; tour += 1) {
    for (const l of LETTRES) {
      const nom = tour === 0 ? l : `${l}${tour}`;
      if (!pris.has(nom)) return nom;
    }
  }
}

export function idLibre(objets: readonly ObjetConstructionDto[], prefixe: string): string {
  const pris = new Set(objets.map((o) => o.id));
  for (let n = 1; ; n += 1) if (!pris.has(`${prefixe}${n}`)) return `${prefixe}${n}`;
}

const arrondi = (v: number): number => Math.round(v * 1e4) / 1e4;

/** La fenêtre et l'échelle du dessin : de quoi passer d'un clic à un point de la figure. */
export interface EchelleDuTrace {
  readonly kx: number;
  readonly ky: number;
  readonly xMin: number;
  readonly yMax: number;
  /** Le `viewBox` du dessin, en unités d'affichage. */
  readonly viewBox: {
    readonly x: number;
    readonly y: number;
    readonly largeur: number;
    readonly hauteur: number;
  };
}

/**
 * L'échelle du dessin d'un tracé. Le cadre « repère » ne dépend que du
 * repère : celle de la figure vide est celle de tout tracé de l'exercice.
 */
export function echelleDuTrace(t: Pick<TraceRelu, 'repere' | 'enonce'>): EchelleDuTrace {
  const figure = versFigureV2(resoudreConstruction(documentDuTrace(t, []), t.enonce), {
    calque: true,
  });
  const { liste, etiquettes } = dessinerPlanAvecEtiquettes(figure, { cadre: 'repere' });
  return { ...etiquettes.echelle, viewBox: liste.viewBox };
}

/** Un point du `viewBox` (unités d'affichage) → un point de la figure. */
export function versFigure(
  e: EchelleDuTrace,
  aff: { readonly x: number; readonly y: number },
): PointV2 {
  return { x: e.xMin + aff.x / e.kx, y: e.yMax - aff.y / e.ky };
}

/**
 * Un toucher de l'outil `outil` au point `p` de la figure. L'aimant est celui
 * de l'atelier (`aimanter`, partagé) : un point existant, une intersection, un
 * objet, un nœud de la grille — jamais la solution.
 */
export function toucherLeBac(
  t: Pick<TraceRelu, 'repere' | 'enonce'>,
  bac: BacASable,
  outil: OutilAuClic,
  p: PointV2,
  rayonAimant: number,
): BacASable {
  const doc = documentDuTrace(t, bac.objets);
  const resolue = resoudreConstruction(doc, t.enonce);
  const pas = resolue.repere.pas;
  const cible = aimanter(p, resolue, rayonAimant, { grille: pas });
  let objets = bac.objets;
  let id: string;
  if (cible.genre === 'point') id = cible.id;
  else {
    const pris = new Set<string>();
    for (const o of resolue.objets.values()) if (o.nom !== undefined) pris.add(o.nom);
    id = idLibre(objets, 'p');
    objets = [
      ...objets,
      {
        id,
        nom: nomLibre(pris),
        def: { type: 'point_libre', x: arrondi(cible.p.x), y: arrondi(cible.p.y) },
      },
    ];
  }
  /* Le polygone se ferme en touchant son premier sommet. */
  if (outil === 'polygone' && bac.enCours.length >= 3 && id === bac.enCours[0]) {
    return fermerLePolygone(bac);
  }
  /* Un geste commence : l'état d'avant est gardé pour Annuler (un point seul ne compte que s'il est neuf). */
  const debut = bac.enCours.length === 0 && (outil !== 'point' || objets !== bac.objets);
  const passe = debut ? [...bac.passe, bac.objets] : bac.passe;
  if (outil === 'point') return { objets, enCours: [], passe };
  const enCours = [...bac.enCours, id];
  if (enCours.length < POINTS_PAR_OUTIL[outil]) return { objets, enCours, passe };
  const [a, b, c] = enCours as [string, string, string];
  const def: ObjetConstructionDto['def'] =
    outil === 'segment'
      ? { type: 'segment', de: a, a: b }
      : outil === 'droite'
        ? { type: 'droite', de: a, a: b }
        : outil === 'vecteur'
          ? { type: 'vecteur', de: a, a: b }
          : outil === 'cercle'
            ? { type: 'cercle_par_point', centre: a, par: b }
            : { type: 'angle', de: a, sommet: b, a: c };
  const prefixe =
    outil === 'cercle' ? 'c' : outil === 'vecteur' ? 'v' : outil === 'angle' ? 'm' : 'd';
  return { objets: [...objets, { id: idLibre(objets, prefixe), def }], enCours: [], passe };
}

/** Ferme le polygone en cours (trois sommets au moins) ; sinon, rien. */
export function fermerLePolygone(bac: BacASable): BacASable {
  if (bac.enCours.length < 3) return bac;
  return {
    objets: [
      ...bac.objets,
      { id: idLibre(bac.objets, 'g'), def: { type: 'polygone', sommets: [...bac.enCours] } },
    ],
    enCours: [],
    /* L'état d'avant le geste est déjà gardé : il a commencé au premier sommet. */
    passe: bac.passe,
  };
}

/** Pose un objet entier (une courbe, un demi-plan, saisis au formulaire). */
export function poserDansLeBac(
  bac: BacASable,
  prefixe: string,
  def: ObjetConstructionDto['def'],
): BacASable {
  return {
    objets: [...bac.objets, { id: idLibre(bac.objets, prefixe), def }],
    enCours: [],
    passe: [...bac.passe, bac.objets],
  };
}

/** Annuler : l'état d'avant le dernier geste — celui en cours, s'il y en a un, est abandonné. */
export function annulerDansLeBac(bac: BacASable): BacASable {
  const precedent = bac.passe[bac.passe.length - 1];
  return precedent === undefined
    ? bac
    : { objets: precedent, enCours: [], passe: bac.passe.slice(0, -1) };
}

/**
 * Lit « a = 2 ; b = −1 ; fonction = cos » en paramètres d'une fonction : des
 * nombres (virgule décimale admise), ou un mot (« cos », « pi/2 ») tel quel —
 * `fonctionCanoniqueV2` les jugera, comme pour une courbe de l'élève.
 */
export function lireParametres(texte: string): Record<string, number | string> {
  const sortie: Record<string, number | string> = {};
  for (const morceau of texte.split(/[;\n]/u)) {
    const [cle, valeur] = morceau.split('=').map((s) => s.trim());
    if (!cle || valeur === undefined || valeur === '') continue;
    const n = Number(valeur.replace(',', '.').replace('−', '-'));
    sortie[cle] = Number.isFinite(n) ? n : valeur;
  }
  return sortie;
}

/** Les objets du bac qui peuvent servir de frontière à un demi-plan : une droite, une courbe. */
export function frontieresPossibles(bac: BacASable): readonly ObjetConstructionDto[] {
  return bac.objets.filter((o) => o.def.type === 'droite' || o.def.type === 'fonction');
}

/** Le nom à montrer d'un objet du bac. */
export function nomDansLeBac(o: ObjetConstructionDto): string {
  return o.nom ?? (estIdEnonce(o.id) ? o.id.slice(2) : o.id);
}

/** Ce que l'élève recevrait pour ce tracé : le jugement du serveur, rien de plus. */
export function jugerLeBac(t: TraceRelu, bac: BacASable): JugementDeTrace {
  return jugerTrace(t.exercice, bac.objets);
}
